import React from 'react';
import MindMap from '../components/MindMap';

/* /guide/copilot — a two-minute tutorial, readable without signing in so the
   announcement email can link straight to it. The "screens" are drawn here
   rather than screenshotted, so they follow the theme and never go stale with
   real member data. */

function You({ children }) {
  return <div className="g-bubble g-you">{children}</div>;
}
function Bot({ children }) {
  return <div className="g-bubble g-bot">{children}</div>;
}
function Screen({ chip, children }) {
  return (
    <div className="g-screen" aria-hidden="true">
      {chip && <div className="g-chip">{chip}</div>}
      {children}
    </div>
  );
}

const SAMPLE_MAP = `Seed term sheet
- Money
  - $1.5M at $12M post
  - 1x non-participating preference
- Control
  - One board seat for the lead
  - Protective provisions
- People
  - 4-year vesting, 1-year cliff
  - 10% option pool, pre-money
- Watch-outs
  - Pool comes out of your share
  - 60-day exclusivity`;

const STEPS = [
  {
    n: 1,
    title: 'Open Copilot and just talk',
    text: 'Tap ✦ Copilot in the menu. There are no commands to learn — write the way you would to a smart colleague.',
    screen: (
      <Screen>
        <You>I'm pitching a fintech seed round next week. What will investors push hardest on?</You>
        <Bot>Usually three things: <b>traction</b> (who pays today), <b>regulation</b> (licences, and who holds the money), and <b>unit economics</b>…</Bot>
      </Screen>
    ),
  },
  {
    n: 2,
    title: 'Paste a link, ask about it',
    text: "Paste any article, announcement or report and ask in your own words. Then keep going — it remembers the page, so you don't paste it again.",
    screen: (
      <Screen>
        <You>https://example.com/stablecoin-rules-2026 — give me the 3 key takeaways</You>
        <Bot>1. Issuers need a licence and full reserves… 2. … 3. …</Bot>
        <You>And what does it mean for a payments startup in the Philippines?</You>
      </Screen>
    ),
  },
  {
    n: 3,
    title: 'Attach a file and chat about it',
    text: 'Tap the 📎 paperclip, upload a PDF, Word doc or text file (or pick one you uploaded before), and ask. Answers point to the page they came from, so you can check them.',
    screen: (
      <Screen chip="📄 Talking about seed-term-sheet.pdf">
        <You>What should I watch out for?</You>
        <Bot>Two things stand out: the option pool is <b>pre-money</b>, so it comes out of your share (p. 2), and there's a <b>60-day exclusivity</b> clause (p. 4)…</Bot>
        <You>Draft a polite reply asking to move the pool post-money.</You>
      </Screen>
    ),
  },
  {
    n: 4,
    title: 'One tap: Summary, FAQ, Watch-outs, Mind map',
    text: "With a file attached, the buttons above the box do the work for you: Summary, Outline, FAQ, Timeline, Watch-outs — and Mind map, which draws the whole document as a map like this one:",
    screen: (
      <>
        <div className="g-tools" aria-hidden="true">
          {['Summary', 'Outline', 'FAQ', 'Timeline', 'Watch-outs', 'Mind map'].map((t) => (
            <span key={t} className={`g-tool${t === 'Mind map' ? ' on' : ''}`}>{t}</span>
          ))}
        </div>
        <div className="g-map"><MindMap text={SAMPLE_MAP} title="Seed term sheet" /></div>
      </>
    ),
  },
  {
    n: 5,
    title: 'Ask about an image or screenshot',
    text: 'Attach a PNG or JPG — a chart, a screenshot of an error, a slide — and ask what it shows. It reads the text and numbers in it.',
    screen: (
      <Screen chip="🖼️ Looking at dashboard.png">
        <You>Why did signups drop in week 3?</You>
        <Bot>The chart shows signups falling from ~420 to ~180 in week 3, the same week the "Pricing" annotation appears…</Bot>
      </Screen>
    ),
  },
  {
    n: 6,
    title: 'It knows your side of Builders’ Network',
    text: 'Copilot can help with B/N itself. Try:',
    list: [
      '"Help me improve my profile" — it interviews you and fills it in (you confirm every change)',
      '"Find me someone who has raised a seed round in payments" — the matcher suggests a real member',
      '"What’s new in my feed?" · "Take me to my introductions" · "Open my chat with Sabrina"',
    ],
  },
];

export default function CopilotGuide() {
  return (
    <div className="legal guide">
      <a className="legal-home" href="/">← Builders' Network</a>
      <h1>Get things done with Copilot</h1>
      <p className="muted-sm">A two-minute guide</p>
      <p>
        Copilot is the AI assistant built into Builders' Network. It reads links and documents for you,
        turns them into summaries, answers and mind maps, and knows your side of the network.
      </p>

      {STEPS.map((s) => (
        <section key={s.n} className="g-step">
          <h2><span className="g-n">{s.n}</span>{s.title}</h2>
          <p>{s.text}</p>
          {s.list && <ul>{s.list.map((t) => <li key={t}>{t}</li>)}</ul>}
          {s.screen}
        </section>
      ))}

      <section className="g-step">
        <h2><span className="g-n">⚡</span>Fast or Thinking?</h2>
        <p>
          At the top of Copilot, <b>Fast / Images</b> is for everyday questions and anything with an image.
          Switch to <b>Thinking</b> for contracts, analysis and anything that needs care — it's slower, but more careful.
        </p>
      </section>

      <section className="g-step">
        <h2><span className="g-n">🔒</span>Your files are private</h2>
        <ul>
          <li>Files you upload are yours alone — other members can't see them unless you choose to share one.</li>
          <li>Copilot runs on our own AI servers. Your files and questions are never sent to outside AI companies or used to train their models.</li>
          <li>15 MB of storage is free (up to 10 MB per document, 8 MB per image). Need more? My profile → Resume &amp; portfolio → Get more storage.</li>
        </ul>
      </section>

      <section className="g-step">
        <h2><span className="g-n">💡</span>Tips</h2>
        <ul>
          <li>Be specific: "summarise for an investor in 5 bullets" beats "summarise".</li>
          <li>Ask follow-ups — it keeps the thread, the link and the file in mind.</li>
          <li>Your past chats are under History, so you can pick one up later.</li>
          <li>Copilot can be wrong, and it isn't legal, financial or investment advice. Check anything important.</li>
        </ul>
      </section>

      <p><a className="g-open" href="/">Open Builders' Network and try it</a></p>
    </div>
  );
}
